import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import {
  cloneElement,
  Dispatch,
  Fragment,
  ReactElement,
  ReactNode,
  SetStateAction,
  SyntheticEvent,
  useEffect,
  useState
} from 'react';
import AgTypography from '../../Styles/AgTypography';
import { AgColor } from '../../Styles/AgColor';
import { useIntl } from 'react-intl';
import { useNavigate, useLocation } from 'react-router-dom';

interface TabPanelProps {
  children?: ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index } = props;

  if (value !== index) {
    return null;
  }
  return (
    <Fragment>
      {children}
    </Fragment>
  );
}

function AgTabProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`
  };
}

export interface AgTabsDataProps {
  hash?: string;
  label?: ReactNode; //required
  wrapped?: boolean;
  startIcon?: ReactElement;
  endIcon?: ReactElement;
  children?: ReactNode; // required
}

const AgTab = (
  {
    tabs,
    hasPersonalDetailsError,
    hasMemberProfileError,
    setValueForSaveBtn,
    updatedValue,
    onTabChange,
  }: { tabs: AgTabsDataProps[],
    hasPersonalDetailsError?: boolean,
    hasMemberProfileError?: boolean,
    setValueForSaveBtn?: Dispatch<SetStateAction<number>>,
    updatedValue?: number,
    // eslint-disable-next-line no-unused-vars
    onTabChange?: (value: number) => void }
) => {
  const messages = useIntl().formatMessage;
  const navigate = useNavigate();
  const location = useLocation();
  const [value, setValue] = useState(0);

  /** Updates tab value from parent */
  useEffect(() => {
    setValue(updatedValue as number);
  }, [updatedValue]);
  
  useEffect(() => {
    if (!tabs[0]?.label) return;
    if (!updatedValue) setValue(0);
    setValueForSaveBtn && setValueForSaveBtn(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabs[0]?.label]);
  
  const handleChange = (event: SyntheticEvent, newValue: number) => {
    setValue(newValue);
    setValueForSaveBtn && setValueForSaveBtn(newValue);
    /** Passes tab value to parent */
    onTabChange && onTabChange(newValue);

    const currentLocation = location.pathname;
    if (tabs[newValue].hash) navigate(`${currentLocation}`, { replace: true, state: { hash: tabs[newValue].hash } });
  };
  
  useEffect(() => {
    const debounce = setTimeout(() => {
      const hash = location.state?.hash;
      if (hash) {
        const index = tabs.findIndex((tab) => tab.hash === hash);
        if (index !== -1) setValue(index);
      }
    });
    return () => clearTimeout(debounce);
  }, [value, tabs, location.state?.hash]);

  return (
    <Box sx={{ width: '100%' }}>
      {(tabs.length === 1 && !tabs[0]?.label) // if so, then it's review details tag
        ? null
        : <Box
          sx={{
            borderBottom: 1,
            borderColor: AgColor.grey4,
            padding: '0 12px',
            '& .MuiTabs-root': {
              minHeight: '40px', // a new value of minHeight is necessary here as per MUI
              maxHeight: '40px' // maxHeight value as per Figma design
            }
          }}
        >
          <Tabs
            value={value}
            onChange={handleChange}
            variant='scrollable'
            scrollButtons='auto'
            aria-label='basic tabs example'
            sx={{
              '& .MuiTabs-scrollButtons.Mui-disabled': {
                opacity: 0.3
              },
              '& .MuiTabs-indicator': {
                backgroundColor: AgColor.secondaryMain,
                height: '4px'
              },
              '& .MuiTab-root': {
                padding: '0 16px',
                minWidth: '73px',
                minHeight: '40px',
                maxHeight: '40px',
                color: AgColor.grey6
              },
              '& .MuiTab-root.Mui-selected': {
                color: AgColor.grey9
              }
            }}
          >
            { // here is tab itself
              tabs.map((tab: AgTabsDataProps, index) => {
                if (!tab || !tab.label) return null;
                return (
                  <Tab
                    key={index}
                    label={
                      <AgTypography
                        dataTestId={tab?.label ? `tab-${tab.label}` : 'tab'}
                        contentStyle={'AP.H4.Bold'}
                        style={{
                          display: 'flex',
                          gap: '10px',
                          color: (
                            (tab?.label === messages({ id: 'PERSONALDETAILS' }) && hasPersonalDetailsError) // if tab name is 'personal details' and it has error
                            || (tab?.label === messages({ id: 'PROFILE_DETAILS' }) && hasMemberProfileError)
                            || (tab?.label === messages({ id: 'ORGANIZATION_DETAILS' }) && hasPersonalDetailsError)
                          )
                            ? AgColor.errorMain
                            : 'inherit'
                        }}
                      >
                        {tab.startIcon && cloneElement(tab.startIcon, { style: { height: '16px', width: '16px' } })}
                        {tab.label}
                        {tab.endIcon && cloneElement(tab.endIcon, { style: { height: '16px', width: '16px' } })}
                      </AgTypography>
                    }
                    wrapped={tab.wrapped ?? false}
                    {...AgTabProps(index)}
                  />
                );
              })
            }
          </Tabs>
        </Box>}

      { // here is content of every tab
        tabs.map((tab, index) => {
          if (!tab || !tab.children) return null;
          return (
            <CustomTabPanel value={value} key={index} index={index}>
              {tab.children}
            </CustomTabPanel>
          );
        })
      }
    </Box>
  );
};

export default AgTab;
